import { Link, useParams } from 'react-router-dom';

import { ClaimIdChip } from 'src/components/editorial/ClaimIdChip';
import { PublicationInstructionBadge, VerdictBadge } from 'src/components/editorial/VerdictBadge';
import { Badge } from 'src/components/ui/Badge';
import { STAGE_LABEL, type ClaimThread, type ThreadStation } from 'src/lib/claimThread';
import { explainCode } from 'src/lib/diagnostics';
import { formatClock } from 'src/lib/time';

/**
 * Composition §12 — one vertical lane, one station per stage. A graph invites exploration; a
 * thread invites reading, and reading is what closes the question.
 *
 * Stations the claim never reached are drawn hollow rather than omitted: the gap is the finding.
 */
export function ClaimThreadLane({ thread }: { thread: ClaimThread }) {
  const { streamId = '' } = useParams<{ streamId: string }>();

  return (
    <div className="thread">
      <ol className="thread__lane">
        {thread.stations.map(station => (
          <li key={station.stage} className="thread__station" data-reached={station.reached}>
            <span className="thread__node" aria-hidden="true" />
            <div className="thread__body">
              <p className="thread__stage">{STAGE_LABEL[station.stage]}</p>
              <StationBody station={station} streamId={streamId} />
            </div>
          </li>
        ))}
      </ol>

      {thread.relationships.length > 0 && (
        <section className="thread__relations">
          <h2 className="section-title">Relaciones con otros claims</h2>
          <p className="thread__relations-note">
            Una relación de contradicción o apoyo es la semilla de una controversia. Se muestra como enlace, no como
            arista: cada claim tiene su propio hilo.
          </p>
          <ul className="thread__relations-list">
            {thread.relationships.map(relation => (
              <li key={`${relation.relationship}-${relation.claimId}`}>
                <Badge tone={relation.relationship === 'contradicts' ? 'contradicted' : 'unresolved'}>
                  {relation.relationship}
                </Badge>
                <Link to={`/stream/${encodeURIComponent(streamId)}/claim/${relation.claimId}`}>
                  {relation.claimId}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function StationBody({ station, streamId }: { station: ThreadStation; streamId: string }) {
  if (!station.reached) {
    return (
      <p className="thread__absent" data-absence={station.absence}>
        {station.note}
      </p>
    );
  }

  switch (station.stage) {
    case 'detected':
      return (
        <>
          <p className="thread__meta mono">
            {station.speaker || 'Hablante desconocido'} · {formatClock(station.timestamp)}
          </p>
          {/* Testimony — reading face (Art Direction §6). */}
          <p className="reading">“{station.excerpt}”</p>
        </>
      );

    case 'normalized':
      return (
        <>
          <p className="thread__meta">
            <ClaimIdChip claimId={station.claim.claim_id} linkToThread={false} />
            <Badge tone="unresolved">{station.claim.claim_type}</Badge>
            <Badge tone="unresolved">{station.claim.modality}</Badge>
          </p>
          <p>{station.claim.claim_text}</p>
          {station.claim.warnings.length > 0 && (
            <p className="thread__warnings">
              {station.claim.warnings.map(warning => (
                <Badge key={warning} tone="partial" title={`${warning} — ${explainCode(warning).what}`}>
                  {explainCode(warning).title}
                </Badge>
              ))}
            </p>
          )}
        </>
      );

    case 'evaluated':
      return (
        <ul className="thread__list">
          {station.matches.map(match => (
            <li key={`${match.chunkId}-${match.criterionId}`}>
              <span className="thread__criterion">{match.criterionId}</span>
              <span className="thread__score">{match.finalScore.toFixed(2)}</span>
              <span className="thread__action">{match.recommendedAction}</span>
            </li>
          ))}
        </ul>
      );

    case 'in_story':
      return (
        <ul className="thread__list">
          {station.stories.map(story => (
            <li key={story.storyId}>
              <Link to={`/stream/${encodeURIComponent(streamId)}/stories#story-${story.storyId}`}>
                {story.headline || story.storyId}
              </Link>
              {story.viaStoryLevel && (
                <span className="thread__caveat">
                  ligado a nivel historia (la herencia por claim no encontró coincidencia de texto)
                </span>
              )}
            </li>
          ))}
        </ul>
      );

    case 'verified':
      return (
        <>
          <p className="thread__meta">
            <VerdictBadge verdict={station.assessment.verdict} confidence={station.assessment.confidence} />
            <PublicationInstructionBadge instruction={station.assessment.publication_instruction} />
          </p>
          {station.assessment.supported_scope && (
            <p className="thread__scope">Alcance sustentado: {station.assessment.supported_scope}</p>
          )}
          {station.assessment.unsupported_scope && (
            <p className="thread__scope thread__scope--unsupported">
              Sin sustento: {station.assessment.unsupported_scope}
            </p>
          )}
          <p className="thread__meta mono">
            {station.assessment.evidence_ids.length} evidencias ·{' '}
            {station.assessment.contradicting_evidence_ids.length} contradictorias ·{' '}
            {station.assessment.provenance_group_count} grupos de procedencia
          </p>
        </>
      );

    case 'drafted':
      return (
        <ul className="thread__list">
          {station.usages.map(usage => (
            <li key={usage.transformationId}>
              <Link to={`/stream/${encodeURIComponent(streamId)}/notes#draft-${usage.transformationId}`}>
                {usage.profileId}
              </Link>
              {usage.blockIndex !== null && <span className="thread__caveat">bloque {usage.blockIndex + 1}</span>}
              {usage.blockText && <p className="thread__block reading">“{usage.blockText}”</p>}
            </li>
          ))}
        </ul>
      );

    case 'reviewed':
      return <p>{station.reviewCount} revisiones editoriales.</p>;

    default:
      return null;
  }
}
